/**
 * Integrytas Group - Base App (MOBILE)
 * User Edit — the touch-first edit sheet from mobile.pen's "User Edit" screen,
 * opened from the mobile Users list. Edits the common scalar fields
 * (display name, preferred locale, active) and saves through the Common Routine
 * (routedUpdate) — §2 single write path. Role-assignment editing stays on the
 * desktop for now; roles are shown read-only here.
 */

import { useEffect, useMemo, useState } from 'react';
import { useChildEditorReadOnly } from '../../lib/child_editors';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { localeName } from '../../i18n/locale_display';
import type { AuthSession } from '../../types/auth';

export interface MobileEditableUser {
  user_id: number;
  display_name: string;
  email: string;
  preferred_locale: string;
  is_active: 'Y' | 'N';
  role_labels: string[];
}

export interface MobileUserEditSheetProps {
  /**
   * May this caller flip the user's is_active? Required, not defaulted: the two
   * call sites answer it differently (the list page from the reporting subtree
   * it already holds, the child drill-down from an RPC), and a default would
   * quietly pick one of them for a future third caller.
   */
  canToggleActive: boolean;
  session: AuthSession;
  user: MobileEditableUser | null;
  onClose: () => void;
  onSaved: () => void;
}

function initialsOf(name: string, email: string): string {
  const base = (name || email || '?').trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2)).toUpperCase();
}

/* rbac-affordance-ok: not a registered page — a sheet whose only entry point is
   the Edit button in MobileUserListPage, which is itself gated on
   hasAccess(session, PAGE_CODE, 'change'). Without that verb `editing` never
   becomes non-null, so save() here is unreachable. It has no page code of its
   own to gate against. */
export function MobileUserEditSheet({ session, user, canToggleActive, onClose, onSaved }: MobileUserEditSheetProps) {
  // Set only when this sheet is opened as a child drill-down the caller may not
  // change. Scoped the same way as the desktop Modal: the BODY goes inert, the
  // dismiss control never does.
  const childReadOnly = useChildEditorReadOnly();
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState('');
  const [locale, setLocale] = useState('EN_US');
  const [active, setActive] = useState(true);
  const [locales, setLocales] = useState<string[]>(['EN_US']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different user is opened.
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name);
    setLocale(user.preferred_locale);
    setActive(user.is_active === 'Y');
    setError(null);
  }, [user]);

  // Active locales for the picker (§8 — from csm_language_master, labels resolved).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb.from('csm_language_master').select('locale_code').eq('is_active', 'Y');
      const codes = ((data ?? []) as { locale_code: string }[]).map((r) => r.locale_code);
      if (!cancelled && codes.length) setLocales(codes);
    })();
    return () => { cancelled = true; };
  }, []);

  const localeOptions = useMemo(
    () => locales.map((c) => ({ code: c, label: localeName(c, c) })),
    [locales],
  );

  const save = async () => {
    if (!user) return;
    if (!displayName.trim()) { setError(t('mobile.users.name_required', 'Display name is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'csm_user_master', 'user_id', user.user_id, {
      display_name: displayName.trim(),
      preferred_locale: locale,
      // Omitted when this caller may not change it. The guard trigger only
      // fires on an actual transition, so resending the current value would
      // pass — but the payload should not claim a decision that was never
      // this caller's to make.
      ...(canToggleActive ? { is_active: active ? 'Y' : 'N' } : {}),
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = user !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.users.edit', 'Edit user')}>
        <div className="m-grabber" />
        {user && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{initialsOf(displayName, user.email)}</span>
              <span>
                <div className="m-nm">{t('mobile.users.edit', 'Edit user')}</div>
                <div className="m-em">{user.email}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-edit-name">{t('mobile.users.display_name', 'Display name')}</label>
                <input
                  id="m-edit-name"
                  className="m-inp"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-edit-locale">{t('mobile.users.locale', 'Preferred locale')}</label>
                <select id="m-edit-locale" className="m-sel" value={locale} onChange={(e) => setLocale(e.target.value)}>
                  {localeOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </div>
              {/* Shown either way — the status is worth reading even when it is not
                  yours to change — but the switch only operates for a caller who may
                  actually flip it. */}
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">
                    {canToggleActive
                      ? t('mobile.users.can_sign_in', 'User can sign in')
                      : t('mobile.users.active_not_yours', 'Only their manager or a super administrator can change this')}
                  </div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  disabled={!canToggleActive}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.users.roles', 'Roles')}</label>
                <div className="m-formrow-ro">
                  {user.role_labels.length === 0 ? t('common.none', 'None') : user.role_labels.join(', ')}
                  <span className="m-formrow-hint"> · {t('mobile.users.roles_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              {!childReadOnly && (
                <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                  {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
                </button>
              )}
              {/* Never disabled by the read-only guard — this is the way out. */}
              <button type="button" className="m-btn m-btn-ghost" onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
